import { ulid } from 'ulid';
import cloneDeep from 'lodash-es/cloneDeep';
import {
    ELEMENT_TYPE,
    FEATURE_FLAG_KEYS,
    UI_LINK_TYPE_ENUM,
} from 'app/shop-ui/constants';
import {
    type FlattedLayout,
    type FlatNode,
    type FlatMap,
    type LayoutNode,
    type LayoutNodeStyleMap,
} from 'shop-ui/types/basic';
import omit from 'lodash-es/omit';

export const LayoutTransformer = {
    toFlatLayout(layout: LayoutNode | LayoutNode[], _parentId: string | null = null) {
        const flatLayoutMap: FlattedLayout = {
            rootIds: [],
            flatMap: {},
        };

        const nodes = Array.isArray(layout) ? layout : [layout];

        const flatten = (node: LayoutNode, parentId: string | null) => {
            const flatNode: FlatNode = {
                id: node.id,
                parentId,
                next: [],
                type: node.type,
                name: node.name,
                enable: node.enable ?? true,
                editable: node.editable ?? true,
                deletable: node.deletable ?? true,
            };

            flatLayoutMap.flatMap[node.id] = flatNode;

            if (!parentId) {
                flatLayoutMap.rootIds.push(node.id);
            }

            if (node.next) {
                flatNode.next = node.next.map((child) => child.id);

                for (const child of node.next) {
                    flatten(child, node.id);
                }
            }
        };

        for (const node of nodes) {
            flatten(node, _parentId);
        }

        return flatLayoutMap;
    },

    toNestLayout(flatMap: FlatMap, rootIds: string[]) {
        const buildNode = (id: string) => {
            const node = flatMap[id];

            return {
                ...Object.entries(omit(node, ['next', 'parentId'])).reduce(
                    (acc, [key, value]) => {
                        if (typeof value === 'boolean' && value === true) {
                            return acc;
                        }

                        return {
                            ...acc,
                            [key]: value,
                        };
                    },
                    {}
                ),
                next:
                    Array.isArray(node.next) && node.next.length > 0
                        ? node.next.map(buildNode)
                        : null,
            };
        };

        return rootIds.map(buildNode) as LayoutNode[];
    },

    getRootId(id: string | null, flatMap: FlatMap) {
        const findParentId = (_id: string) => {
            const node = flatMap[_id];

            if (node.parentId) {
                return findParentId(node.parentId);
            }

            return _id;
        };

        return id ? findParentId(id) : null;
    },

    getSelectableIdSet(
        selectedElementId: string | null,
        flatMap: FlatMap,
        rootIds: string[]
    ) {
        const selectableIdSet = new Set<string>();

        for (const firstLevelSectionId of rootIds) {
            selectableIdSet.add(firstLevelSectionId);
        }

        const rootId = LayoutTransformer.getRootId(selectedElementId, flatMap);

        if (rootId) {
            const collect = (id: string) => {
                const node = flatMap[id];

                if (
                    node.type !== ELEMENT_TYPE.GRID_SECONDARY_LEVEL &&
                    node.type !== ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER
                ) {
                    selectableIdSet.add(id);
                }

                if (node.next) {
                    node.next.forEach(collect);
                }
            };

            collect(rootId);
        }

        return selectableIdSet;
    },

    getNodeChain(id: string, flatMap: FlatMap) {
        const nodeChain: string[] = [];

        const ancestorChain: string[] = [];

        const nodeParentId = flatMap[id].parentId;

        const collectNextNode = (_id: string) => {
            nodeChain.push(_id);

            if (flatMap[_id]?.next) {
                flatMap[_id].next.forEach(collectNextNode);
            }
        };

        const collectAncestor = (_id: string) => {
            ancestorChain.unshift(_id);

            if (flatMap[_id]?.parentId) {
                collectAncestor(flatMap[_id].parentId);
            }
        };

        collectNextNode(id);

        collectAncestor(id);

        return {
            nodeChain,
            nodeParentId,
            ancestorChain,
        };
    },

    duplicate(flatMap: FlatMap, elementId: string) {
        const resultFlatMap = {} as FlatMap;

        const originalIdToNewIdMapping: string[][] = [];

        let newElementId = '';

        const isSectionClone = flatMap[elementId].parentId === null;

        const run = (originalId: string, parentId?: string) => {
            const node = flatMap[originalId];

            const newId = ulid();

            if (!newElementId) {
                newElementId = newId;
            }

            originalIdToNewIdMapping.push([originalId, newId]);

            resultFlatMap[newId] = {
                ...cloneDeep(node),
                id: newId,
            };

            const _parentId = parentId || node.parentId;

            if (_parentId) {
                const parentNode = resultFlatMap[_parentId];

                resultFlatMap[newId].parentId = _parentId;

                if (!parentNode) {
                    resultFlatMap[_parentId] = flatMap[_parentId];
                }

                if (isSectionClone) {
                    resultFlatMap[_parentId].next = resultFlatMap[_parentId].next.map(
                        (nextId) => (nextId === originalId ? newId : nextId)
                    );
                }
            }

            if (node.next) {
                node.next.forEach((nextId) => {
                    run(nextId, newId);
                });
            }
        };

        run(elementId);

        return {
            newFlatMap: resultFlatMap,
            newElementId,
            originalIdToNewIdMapping,
        };
    },

    preparePageMetaIds(styleMap: LayoutNodeStyleMap) {
        const pageIdMap = {} as {
            [key: string]: string;
        };

        for (const [id, style] of Object.entries(styleMap)) {
            const shouldCache =
                style &&
                Object.prototype.hasOwnProperty.call(style, FEATURE_FLAG_KEYS.UI_LINK) &&
                Object.prototype.hasOwnProperty.call(
                    style,
                    FEATURE_FLAG_KEYS.UI_LINK_TYPE
                ) &&
                style[FEATURE_FLAG_KEYS.UI_LINK_TYPE] === UI_LINK_TYPE_ENUM.PAGE;

            const pageId = shouldCache ? style[FEATURE_FLAG_KEYS.UI_LINK] : null;

            if (shouldCache && pageId) {
                pageIdMap[id] = pageId;
            }
        }

        const pageIds = Array.from(new Set(Object.values(pageIdMap)));

        return pageIds;
    },
};
